import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ShieldAlert, FileWarning } from "lucide-react";
import { toast } from "sonner";

/**
 * Dialog de publication d'un CAS CLINIQUE — checklist anonymisation 8 points obligatoire.
 * Soumet le post avec type="clinical_case" → modération humaine systématique (status pending_review).
 * CDC v1.0 US-11 + §5 conformité UX réglementaire.
 */
export default function ClinicalCaseDialog({ open, onOpenChange, onPublished }) {
  const [points, setPoints] = useState([]);
  const [checks, setChecks] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/posts/anonymization-checklist").then(({ data }) => {
      setPoints(data.points || []);
      setChecks(new Array(data.points?.length || 8).fill(false));
    }).catch(() => {});
  }, [open]);

  const allChecked = checks.length > 0 && checks.every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!allChecked) return toast.error("Vous devez cocher les 8 points d'anonymisation.");
    if (!title.trim() || content.trim().length < 100) {
      return toast.error("Titre obligatoire + contenu de 100 caractères minimum.");
    }
    setSubmitting(true);
    try {
      await api.post("/posts", {
        type: "clinical_case",
        title: title.trim(),
        content: content.trim(),
        anonymization_checklist: checks,
      });
      toast.success("Cas clinique soumis · validation par l'équipe médicale sous 4 h");
      setTitle(""); setContent(""); setChecks(checks.map(() => false));
      onOpenChange(false);
      onPublished?.();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-amber-600" />
          Publier un cas clinique anonymisé
        </DialogTitle>
        <DialogDescription>
          Les cas cliniques sont validés systématiquement par l'équipe médicale (SLA 4 h).
          Vous devez confirmer les 8 points d'anonymisation ci-dessous avant la soumission.
        </DialogDescription>

        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 flex items-start gap-3 mt-2" role="alert">
          <FileWarning className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <strong className="font-bold">Avertissement CNIL · RGPD :</strong> tout cas clinique publié doit être <strong>totalement anonymisé</strong>.
            La responsabilité légale repose sur l'auteur. La modération humaine est obligatoire.
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4 mt-3">
          <div>
            <label className="text-sm font-semibold text-slate-700 block mb-1">Titre du cas clinique <span className="text-red-500">*</span></label>
            <Input
              required
              maxLength={120}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Découverte fortuite d'un nodule pulmonaire chez patient de 60 ans"
              data-testid="cc-title-input"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 block mb-1">Description anonymisée <span className="text-red-500">*</span></label>
            <Textarea
              required
              rows={6}
              minLength={100}
              maxLength={5000}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Décrivez le cas sans données identifiantes — antécédents, examen, démarche diagnostique, conclusion…"
              data-testid="cc-content-input"
            />
            <div className="text-xs text-slate-400 mt-1">{content.length} / 5000 caractères · minimum 100</div>
          </div>

          <fieldset className="border border-slate-200 rounded-lg p-4">
            <legend className="text-sm font-bold text-deep px-2">Checklist anonymisation — les 8 points <span className="text-red-500">*</span></legend>
            <ul className="space-y-2.5 mt-2">
              {points.map((label, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Checkbox
                    id={`cc-point-${i}`}
                    checked={checks[i] || false}
                    onCheckedChange={(v) => setChecks((arr) => arr.map((x, idx) => idx === i ? !!v : x))}
                    data-testid={`cc-point-${i}`}
                    className="mt-0.5"
                  />
                  <label htmlFor={`cc-point-${i}`} className="text-sm text-slate-700 leading-snug cursor-pointer">{label}</label>
                </li>
              ))}
            </ul>
            <div className="text-xs text-slate-500 mt-3">
              {checks.filter(Boolean).length} / {points.length} points validés
            </div>
          </fieldset>

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1" data-testid="cc-cancel-btn">Annuler</Button>
            <Button
              type="submit"
              disabled={!allChecked || submitting}
              className="flex-1 btn-coral disabled:opacity-50"
              data-testid="cc-submit-btn"
            >
              {submitting ? "Soumission…" : "Soumettre pour modération"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
